import { Modal } from 'app-components';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage,useIntl } from 'react-intl';
import styled from 'styled-components';
import Dropdown from '../../../../lib/inputs/Dropdown';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';
import { convertLocalToServerTimestamp } from '../../../../utils/dates/dateMethods';
import useApiHandling from '../../hooks/useApiHandling';
import {
    ApplicationType,
    ConditionalRendering,
    DEFAULT_PERSON_ID,
    eventDateValidation,
    GIButton,
    ICsrOverideSpecialEnrollmentModalProps,
    INPUT_TYPES,
    SPECIAL_ENROLLMENT_DETAILS_TABLE_HEADER_DATA,
    useAppSelector,
    ValidationErrorMessage,
    YesNo,
} from '../../utils';

const StyledModal = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .f-center {
        display: flex;
        align-items: center;
        column-gap: 10px;
    }

    .gap-2 {
        display: flex;
        gap: 10px;
    }
`;

const ContentAlert = styled.div<{ hasInfoBar: boolean }>`
    padding: 8px 16px;
    background: ${({ theme, hasInfoBar }) => (hasInfoBar ? theme.infoLightest : theme.primaryLightest)};
    border-left: ${({ theme, hasInfoBar }) => (!hasInfoBar ? `3px solid ${theme.primaryMain}` : '')};
    border-radius: 5px;
    color: ${({ theme, hasInfoBar }) => (!hasInfoBar ? theme.primaryDarker : '')};
    margin-bottom: 20px;
    a {
        text-decoration: underline;
        &:visited {
            color: ${({ theme }) => theme.primaryDark};
        }
    }
`;

const DetailTable = styled.table`
    width: 100%;
    .mendatory {
        color: ${({ theme }) => theme.dangerMain};
        margin-left: 5px;
    }
    thead {
        height: 40px;
        background-color: ${({ theme }) => theme['bg-color-header']};
    }
    th {
        align-content: baseline;
    }
    td {
        vertical-align: middle;
    }
    .w-220 {
        width: 220px;
    }
    .header {
        border-top-right-radius: 80px 80px;
        th {
            padding: 10px;
        }
    }
`;

const StyledDropdown = styled.div`
    .gi-dropdown__select {
        width: ${({ width }) => width};
    }
    .gi-dropdown {
        text-align: left;
        margin: 0;
        padding: 0 14px 0 0;
        label {
            display: block;
        }
    }
`;

const StyledField = styled.div`
    .date-picker {
        margin-top: 0;
        padding: 8px;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-input {
        text-align: left;
        margin: 0;
    }
`;

const ErrorDiv = styled.div`
    .error--msg {
        margin: 0 !important;
        padding: 3px 8px !important;
        width: 87% !important;
    }
`;

const isApplicantRelevant = (
    applicant: { showOnUI: string; personId: number },
    applicationType: string,
): boolean =>
    applicant.showOnUI === YesNo.Y &&
    (applicationType === ApplicationType.SEP ||
        (applicationType === ApplicationType.QEP && applicant.personId === DEFAULT_PERSON_ID));

/** Use for DatePicker `value` only — avoids passing Invalid Date into react-datepicker (throws on format). */
const getEventDateValue = (eventDate: string | Date | null | undefined): Date | null => {
    if (eventDate == null || eventDate === '' || eventDate === 'Invalid Date') return null;
    const date = eventDate instanceof Date ? eventDate : new Date(eventDate);
    return isNaN(date.getTime()) ? null : date;
};

const CsrOverrideEnrollmentModal: React.FC = ({
    closeModal,
    data,
    application,
}: ICsrOverideSpecialEnrollmentModalProps) => {
    const [formData, setFormData] = useState({ ...data });
    const [error, setError] = useState<{
        [key: string]: { within60Days: boolean; withinPast60Days: boolean; withinFuture60Days: boolean };
    }>({});
    const { getSepDateDetails } = useApiHandling();
    const isState = useSelector((state: any) => state?.common?.environment?.isState);
    const { gracePeriod } = useAppSelector(state => state.capApplications.appControllerInfo);
    const intl = useIntl();

    function onClose() {
        closeModal('csrOverrideEnrollmentModal');
    }

    function onSubmit() {
        getSepDateDetails(
            formData,
            data?.caseNumber,
            () => {
                onClose();
            },
            gracePeriod,
            application,
        );
    }

    const checkEventChange = eventData => {
        return eventData.some(applicant =>
            applicant.events.some(
                event => event.previousEventId !== event.eventId || event.previousEventDate !== event.eventDate,
            ),
        );
    };

    const localDates = useAppSelector(state => state.common.environment.localDates);
    const { serverDate } = localDates || {};
    const timezoneOffsetMs = Number(localDates?.timezoneOffsetMs) || 0;
    const dstSavingsMs = Number(localDates?.dstSavingsMs) || 0;
    const { specialEnrollmentInfo } = useAppSelector(state => state.capApplications.appControllerInfo);

    const handleChange = (key, applicantInd, eventInd, val) => {
        const modifiedData = { ...formData };
        modifiedData.applicants[applicantInd].events[eventInd][key] =
            key === 'eventId' ? val : convertLocalToServerTimestamp(val, timezoneOffsetMs, dstSavingsMs, MM_SLASH_DD_SLASH_YYYY);
        setFormData(modifiedData);
        eventDateValidation({
            formData,
            applicantInd,
            eventInd,
            eventId: key === 'eventId' ? val : modifiedData.applicants[applicantInd].events[eventInd].eventId,
            eventDate: key === 'eventId' ? modifiedData.applicants[applicantInd].events[eventInd].eventDate : val,
            error,
            setError,
            serverDate,
        });
    };

    return (
        <Modal id="override_special_enrollment_details_modal">
            <Modal.Header
                id="override_special_enrollment_details_modal_title"
                titleId="cap.applications.modal.overrideSpecialEnrollmentDetails.title"
                onHide={onClose}
            />
            <Modal.Body>
                <ContentAlert hasInfoBar={isState?.ID}>
                    <FormattedMessage id="cap.application.modal.overrideSpecialEnrollmentDetailsModalAlert.text" />{' '}
                    <span>
                        <a href={specialEnrollmentInfo} target="_blank">
                            <FormattedMessage id="cap.application.modal.overrideSpecialEnrollmentDetailsModalAlert.text.here" />
                        </a>
                    </span>
                    <br />
                    <FormattedMessage id="cap.application.modal.overrideSpecialEnrollmentDetailsModalAlert.text2" />
                    <br />
                    <FormattedMessage id="cap.application.modal.overrideSpecialEnrollmentDetailsModalAlert.text3" />
                </ContentAlert>
                <DetailTable>
                    <thead className={isState?.ID ? 'header' : ''}>
                        <tr>
                            {SPECIAL_ENROLLMENT_DETAILS_TABLE_HEADER_DATA.map(item => (
                                <th data-testid={item.id} key={item.id}>
                                    <FormattedMessage id={item.text} />
                                    <ConditionalRendering isTrue={item.isMandatory}>
                                        <span className="mendatory">*</span>
                                    </ConditionalRendering>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {formData.applicants.map((applicant, index) => (
                            <>
                                {applicant.events.map((event, ind) => {
                                    const withinPast60Days = error[`${index}${ind}`]?.withinPast60Days;
                                    const withinFuture60Days = error[`${index}${ind}`]?.withinFuture60Days;
                                    const within60Days = error[`${index}${ind}`]?.within60Days;
                                    const hasError = withinPast60Days || withinFuture60Days || within60Days;

                                    return (
                                        <ConditionalRendering
                                            isTrue={isApplicantRelevant(
                                                applicant,
                                                formData.applicationType,
                                            )}
                                            key={`applicant_event_${index}_${ind}`}
                                        >
                                            <>
                                                <tr>
                                                    <td>
                                                        {applicant.firstName} {applicant.middleName}{' '}
                                                        {applicant.lastName}{' '}
                                                        <ConditionalRendering
                                                            isTrue={applicant.personId === DEFAULT_PERSON_ID}
                                                        >
                                                            <span>(Primary)</span>
                                                        </ConditionalRendering>
                                                    </td>
                                                    <td>
                                                        <StyledDropdown width={'220px'}>
                                                            <Dropdown
                                                                label={{
                                                                    text: `${applicant.firstName} ${intl.formatMessage({ id: 'cap.application.modal.overrideSpecialEnrollmentDetailsModal.table.header.eventType' })}`,
                                                                    labelClass:['sr-only']
                                                                }}
                                                                inputData={{
                                                                    inputClasses: ['gi-dropdown__select grid-col-4'],
                                                                    name: 'exchange_eligibility',
                                                                    options: event.eventList?.map(
                                                                        ({ eventId, eventNameLabel }) => ({
                                                                            value: eventId,
                                                                            text: eventNameLabel,
                                                                        }),
                                                                    ),
                                                                    type: INPUT_TYPES.DROPDOWN,
                                                                    value: event.eventId,
                                                                    isInputRequired: false,
                                                                }}
                                                                inputActions={{
                                                                    handleFieldChange: val => {
                                                                        handleChange('eventId', index, ind, val);
                                                                    },
                                                                }}
                                                            />
                                                        </StyledDropdown>
                                                    </td>
                                                    <td>
                                                        <div className="w-220">
                                                            <StyledField>
                                                                <label className={'sr-only'} htmlFor='dob'>
                                                                    {`${applicant.firstName} ${intl.formatMessage({ id: 'cap.application.modal.overrideSpecialEnrollmentDetailsModal.table.header.eventDate' })}`}
                                                                </label>
                                                                <DatePicker
                                                                    labelText=''
                                                                    wrapperClasses={['date-picker']}
                                                                    isRequired
                                                                    id="dob"
                                                                    type="text"
                                                                    pattern="MM/DD/YYYY"
                                                                    placeholder="MM/DD/YYYY"
                                                                    value={getEventDateValue(event.eventDate)}
                                                                    withMask={true}
                                                                    onDateChange={val =>
                                                                        handleChange('eventDate', index, ind, val)
                                                                    }
                                                                />
                                                            </StyledField>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <ConditionalRendering isTrue={hasError}>
                                                    <tr>
                                                        <td></td>
                                                        <td colSpan={2}>
                                                            <ErrorDiv>
                                                                <ValidationErrorMessage
                                                                    isVisible={withinPast60Days}
                                                                    messageText={
                                                                        'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.withinpast60days'
                                                                    }
                                                                />
                                                            </ErrorDiv>
                                                            <ErrorDiv>
                                                                <ValidationErrorMessage
                                                                    isVisible={withinFuture60Days}
                                                                    messageText={
                                                                        'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.withinfuture60days'
                                                                    }
                                                                />
                                                            </ErrorDiv>
                                                            <ErrorDiv>
                                                                <ValidationErrorMessage
                                                                    isVisible={within60Days}
                                                                    messageText={
                                                                        'cap.application.modal.overrideSpecialEnrollmentDetailsModal.error.withinpastorfuture60days'
                                                                    }
                                                                />
                                                            </ErrorDiv>
                                                        </td>
                                                    </tr>
                                                </ConditionalRendering>
                                            </>
                                        </ConditionalRendering>
                                    );
                                })}
                            </>
                        ))}
                    </tbody>
                </DetailTable>
            </Modal.Body>
            <Modal.Controls>
                <StyledModal>
                    <div className="gap-2">
                        <GIButton
                            id="continue"
                            labelId="cap.applications.modal.button.label.continue"
                            onClickHandler={onSubmit}
                            isDisabled={
                                Object.values(error).some(
                                    obj => obj.within60Days || obj.withinPast60Days || obj.withinFuture60Days,
                                ) ||
                                !formData.applicants.some(applicant =>
                                    isApplicantRelevant(applicant, formData.applicationType),
                                ) ||
                                !checkEventChange(formData.applicants)
                            }
                        ></GIButton>
                        <GIButton
                            id="cancel"
                            labelId="cap.applications.modal.button.label.cancel"
                            variant="secondary"
                            onClickHandler={onClose}
                        ></GIButton>
                    </div>
                </StyledModal>
            </Modal.Controls>
        </Modal>
    );
};

export default CsrOverrideEnrollmentModal;